import { useState, useCallback } from 'react';

import { useQuestionBankStore } from '../../../question-bank/store';
import { toQuestionBankType } from '../../../question-bank/types';
import { useDynamicDocumentStore } from '../../filmstrip/documents';
import { AI_TEST_COURSE_ID } from '../../quiz/aiTestCourse';
import { aiQuizTitle, quizBodyFromBank, quizMetaLine, sharedLevelWord } from '../../quiz/fromBank';
import { useSelectUnit } from '../../useSelectUnit';
import { AI_MESSAGES } from '../constants/index.ts';
import { mapExportedToDbQuestions } from '../embed/index.ts';
import type { QsExportedData } from '../embed/types.ts';

/**
 * Hook xử lý nghiệp vụ nhập kết quả câu hỏi trắc nghiệm từ AI Quiz Supporter:
 * - Chuẩn hóa và làm sạch dữ liệu câu hỏi qua quizMapper
 * - Mở ngay bộ câu hỏi lên bảng dưới dạng MỘT tài liệu mới
 * - Lưu danh sách câu hỏi lên kho bài tập (IZTeach /question, dùng chung cả trường) kèm sẵn liên kết tài liệu đó
 * - Gọi callback để hiển thị toast và highlight các câu hỏi vừa thêm
 *
 * Thứ tự tài liệu trước, lưu sau là có chủ đích: id tài liệu đi kèm ngay trong từng lệnh tạo câu hỏi,
 * nên không phải gọi thêm một request gắn bài giảng cho từng câu hỏi.
 *
 * Kho không có lệnh ghi hàng loạt: câu hỏi được gửi lần lượt từng câu, dừng ở câu lỗi đầu tiên.
 * Lưu được một phần thì báo đúng một phần, không báo thành công.
 *
 * @param onClose - Callback đóng modal sau khi import thành công
 * @param onImportSuccess - Callback khi import thành công, trả về danh sách id câu hỏi đã tạo
 *   và cờ cho biết bộ câu hỏi đã được mở lên bảng hay chưa
 */
export function useQuizImport(
  onClose: () => void,
  onImportSuccess?: (ids: string[], openedOnBoard: boolean) => void,
) {
  const [notice, setNotice] = useState<string>('');
  const [noticeKind, setNoticeKind] = useState<'success' | 'error' | 'warning'>('success');
  const selectUnit = useSelectUnit();

  const handleQuizImport = useCallback(
    async (data: QsExportedData) => {
      const questions = mapExportedToDbQuestions(data);

      // Kiểm tra tính hợp lệ: cảnh báo nếu không có câu hỏi nào để nhập
      if (questions.length === 0) {
        setNoticeKind('warning');
        setNotice(AI_MESSAGES.QUIZ_EMPTY_IMPORT);
        return;
      }

      const inputs = questions.map((question) => ({
        content: question.content,
        type: toQuestionBankType(question.type),
        level: question.level,
        options: question.options,
        explanation: question.explanation,
        chatId: data.chatId,
      }));

      // 1. Điểm sửa lỗi: bộ câu hỏi trở thành một tài liệu trên bảng ngay lập tức.
      // Kho bài tập vẫn giữ bản sao - đây là bổ sung, không thay thế.
      const now = Date.now();
      // Chỉ dùng dữ liệu export thật sự có: số câu và độ khó. Môn/lớp/chủ đề KHÔNG nằm trong
      // QsExportedData, nên tuyệt đối không suy đoán - xem ghi chú ở quizMetaLine.
      const body = quizBodyFromBank(
        aiQuizTitle(now),
        quizMetaLine('AI Trợ Giảng', inputs.length, sharedLevelWord(inputs)),
        inputs,
      );
      // MỘT tài liệu duy nhất: id suy ra từ chính bộ câu hỏi nên nhập lại cùng bộ sẽ mở lại
      // đúng tài liệu cũ thay vì đẻ thêm bản sao - id này cũng là documentKey của lớp mực.
      const document = useDynamicDocumentStore.getState().openQuiz(body);

      // 2. Lưu câu hỏi lên kho bài tập, gắn sẵn liên kết bài kiểm tra vừa tạo
      const lessonRef = { courseId: AI_TEST_COURSE_ID, unitId: document.id, unitName: document.title };
      // Máy chủ mới là nơi cấp id, nên đây chính là id mà trang Kho bài tập dùng để highlight
      const ids = await useQuestionBankStore.getState().addMany(
        inputs.map((item) => ({ ...item, lessonRef })),
      );

      // Không lưu được câu nào: giữ modal để thầy/cô thử lại - mở lại cùng bộ sẽ ra đúng tài liệu cũ
      if (ids.length === 0) {
        setNoticeKind('error');
        setNotice(useQuestionBankStore.getState().error || AI_MESSAGES.QUIZ_SAVE_FAILED);
        return;
      }

      // Lưu được một phần: bảng vẫn mở bộ câu hỏi, nhưng modal giữ lại để nói rõ câu nào chưa lưu
      if (ids.length < inputs.length) {
        selectUnit({ unitId: document.id, unitName: document.title });
        setNoticeKind('warning');
        setNotice(useQuestionBankStore.getState().error || AI_MESSAGES.QUIZ_SAVE_FAILED);
        return;
      }

      onClose();
      // 3. Điều hướng bảng sang tài liệu vừa tạo
      selectUnit({ unitId: document.id, unitName: document.title });
      if (onImportSuccess) {
        onImportSuccess(ids, true);
      }
    },
    [onClose, onImportSuccess, selectUnit]
  );

  return {
    notice,
    noticeKind,
    setNotice,
    handleQuizImport,
  };
}
